import { Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setSearchValue, setSelectedItem } from 'app/actions/driver';
import { IconButton, Image, InputSearch, Scrollbar } from 'app/components';
import { DriverDialog, DriverForm, DriverList } from 'app/pages/Driver';
import { useAsyncAction } from 'app/services';
import { driverDelete, driverLoad, driverUpdate } from 'app/store/driver';
import { autocolunItemsLoad, divisionItemsLoad } from 'app/store/transport';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Driver = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { items, selectedItem, searchValue } = useSelector((state) => state.driver);

  const [dialogOpen, setDialogOpen] = useState(false);
  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(driverLoad);

  const { asyncAction: fetchAutocolunItems, isLoading: isAutocolunItemsLoading } = useAsyncAction(autocolunItemsLoad);
  const { asyncAction: fetchDivisionItems, isLoading: isDivisionItemsLoading } = useAsyncAction(divisionItemsLoad);
  const isLoading = isFetchLoading || isAutocolunItemsLoading || isDivisionItemsLoading;

  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(driverUpdate, [selectedItem]);
  const {
    asyncAction: deleteSubmit,
    isLoading: isDeleteLoading,
    isSuccessful: isDeleteSuccessful,
    isError: isDeleteError,
  } = useAsyncAction(driverDelete, [selectedItem]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (params) => {
    updateSubmit(selectedItem.id, params);
  };

  const handleDeleteClick = () => {
    deleteSubmit(selectedItem.id);
  };

  const handleDriverSearchChange = (value) => dispatch(setSearchValue(value));

  useEffect(() => {
    fetchData();
    fetchDivisionItems();
    fetchAutocolunItems();
    return () => dispatch(setSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isLoading && items.length ? (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
              })}>
              <InputSearch placeholder='ФИО водителя' value={searchValue} onChange={handleDriverSearchChange} />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <DriverDialog open={dialogOpen} onClose={handleDialogClose} />
            </Stack>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
              <DriverList />
            </Stack>
          </React.Fragment>
        ) : (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться водители транспорта
            </Typography>
          </Stack>
        )}
        {isLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                padding: theme.spacing(4, 2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <Image name='avatar-driver.png' alt='avatar-driver' />
                </Avatar>
                <Typography variant='body1'>{selectedItem.name}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  form: {
                    width: '100%',
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(1),
                    '.MuiTypography-h6': {
                      fontSize: '1rem',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Информация</Typography>
                  <Typography variant='caption'>Основная информация водителя и его подразделение</Typography>
                </Stack>
                <DriverForm ref={formRef} defaultValues={selectedItem} onSubmit={handleFormSubmit} />
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateLoading && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  marginTop: theme.spacing(3),
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  '.MuiAlert-root': {
                    marginTop: theme.spacing(0),
                    marginBottom: theme.spacing(3),
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(3),
                    '.MuiTypography-h6': {
                      fontSize: '1rem',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Удалить водителя</Typography>
                  <Typography variant='caption'>
                    Можно удалить данного водителя, но это действие необратимо. Пожалуйста, будьте уверены, что Вы хотите это сделать
                  </Typography>
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(1),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {isDeleteError && (
                    <Alert severity='error'>
                      Произошла ошибка при удалении водителя. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {isDeleteSuccessful && <Alert severity='success'>Водитель успешно удален.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(0),
                  })}>
                  <LoadingButton color='error' variant='contained' onClick={handleDeleteClick} loading={isDeleteLoading} disableElevation>
                    Удалить водителя
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите водителя из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Driver;
